"use client";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type EffectiveRow = { flag_key: string; enabled: boolean };

async function fetchEffectiveMap(supabaseClient: typeof supabase) {
  // try user-aware RPC first
  try {
    const { data: u } = await supabaseClient.auth.getUser();
    const userId = u?.user?.id;
    if (userId) {
      const { data, error } = await supabaseClient
        .rpc("effective_feature_flags", { p_user_id: userId });
      if (error) throw error;
      const m = new Map<string, boolean>();
      (data ?? []).forEach((r: EffectiveRow) => m.set(r.flag_key, !!r.enabled));
      return m;
    }
  } catch (_) {}
  // fallback: global only
  const { data, error } = await supabaseClient
    .from("feature_flags")
    .select("key, enabled_global");
  if (error) throw error;
  const m = new Map<string, boolean>();
  (data ?? []).forEach((r: any) => m.set(r.key, !!r.enabled_global));
  return m;
}

/**
 * Parent-child rule:
 * - If parent is ON: child is always ON (parent forces children)
 * - If parent is OFF: child follows its own setting
 * - If parent is missing: child follows its own setting (compat)
 * - Top-level keys without hyphen gate themselves
 */
export function useFeatureFlag(key: string) {
  const familyKey = key.includes("-") ? key.split("-")[0] : null;

  const { data: map } = useQuery({
    queryKey: ["feature-flags-effective"],
    queryFn: () => fetchEffectiveMap(supabase),
    staleTime: 60_000,
  });

  if (!map) return false;

  if (!familyKey) return !!map.get(key);

  const parentEnabled = map.has(familyKey) ? !!map.get(familyKey) : false;
  const childEnabled = !!map.get(key);
  
  // If parent is ON, child is always ON (parent forces children)
  if (parentEnabled) return true;
  
  // If parent is OFF, child follows its own setting
  return childEnabled;
}

export function useFeatureFamily(parentKey: string, childKeys: string[]) {
  const { data: map } = useQuery({
    queryKey: ["feature-flags-effective"],
    queryFn: () => fetchEffectiveMap(supabase),
    staleTime: 60_000,
  });

  const out: Record<string, boolean> = {};
  if (!map) return out;
  
  const parentEnabled = map.has(parentKey) ? !!map.get(parentKey) : false;
  
  childKeys.forEach((k) => { 
    const childEnabled = !!map.get(k);
    // If parent is ON, child is always ON (parent forces children)
    // If parent is OFF, child follows its own setting
    out[k] = parentEnabled || childEnabled;
  });
  
  return out;
}

export type ParentState = 'on' | 'off' | 'partial';

export function useParentState(parentKey: string, childKeys: string[]): ParentState {
  const { data: map } = useQuery({
    queryKey: ["feature-flags-effective"],
    queryFn: () => fetchEffectiveMap(supabase),
    staleTime: 60_000,
  });

  if (!map) return 'off';
  
  const parentEnabled = map.has(parentKey) ? !!map.get(parentKey) : false;
  
  // If parent is explicitly ON, return 'on'
  if (parentEnabled) return 'on';
  
  // If parent is OFF, check children to determine partial state
  const childStates = childKeys.map(k => !!map.get(k));
  const enabledChildren = childStates.filter(Boolean).length;
  const totalChildren = childStates.length;
  
  if (enabledChildren === 0) return 'off';
  if (enabledChildren === totalChildren) return 'on';
  return 'partial';
}